import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  datevEmployeeService,
  DatevApp,
  DatevConnectionStatus,
  DatevApiError
} from '../../../services/datev/DatevEmployeeService';

interface CustomerDatevPanelProps {
  customerId: string;
  // Gespeicherte Stammdaten-Werte (nicht der ungespeicherte Formularstand),
  // weil das Backend den OAuth-Scope aus dem Kundensatz ableitet.
  beraternummer: string;
  mandantennummer: string;
  // Wird nach Verbinden/Trennen aufgerufen, damit das Fenster beim Schliessen
  // die Liste aktualisiert.
  onUpdate?: () => void;
}

// Die vier DATEV-Dienste. Werte muessen zum DatevApp-Typ und zum Backend-Mapping
// passen (identisch zu DATEV_APP_LIST in DatevSettings).
const DATEV_APPS: Array<{ app: DatevApp; label: string }> = [
  { app: 'payrollreports', label: 'Lohnauswertungsdatenservice' },
  { app: 'exports', label: 'Lohnergebnisdatenservice' },
  { app: 'exchange', label: 'Lohnaustauschdatenservice' },
  { app: 'eau', label: 'Elektronische AU-Bescheinigung' }
];

type StatusMap = Partial<Record<DatevApp, DatevConnectionStatus>>;

export const CustomerDatevPanel: React.FC<CustomerDatevPanelProps> = ({
  customerId,
  beraternummer,
  mandantennummer,
  onUpdate
}) => {
  const [statusByApp, setStatusByApp] = useState<StatusMap>({});
  const [busyApp, setBusyApp] = useState<DatevApp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const popupRef = useRef<Window | null>(null);
  // Merkt sich, dass gerade ein Verbindungsversuch laeuft, damit der Fokus-Handler
  // ohne Abhaengigkeit vom React-State reagieren kann.
  const connectingRef = useRef(false);

  const loadStatuses = useCallback(async () => {
    try {
      const entries = await Promise.all(
        DATEV_APPS.map(async ({ app }) => {
          const status = await datevEmployeeService.getConnectionStatus(customerId, app);
          return [app, status] as const;
        })
      );
      const next: StatusMap = {};
      entries.forEach(([app, status]) => {
        next[app] = status;
      });
      setStatusByApp(next);
    } catch (err) {
      console.error('[CustomerDatevPanel] Status-Load-Fehler:', err);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Aufraeumen beim Verlassen
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  // Primaerer Trigger: Sobald das Login-Popup schliesst, kehrt der Fokus zum
  // Kunden-Fenster zurueck. Darauf laden wir den Status neu. Das haengt an keiner
  // Popup-Referenz und ist damit robust gegen strenge Browser-Richtlinien.
  useEffect(() => {
    const onFocus = () => {
      if (!connectingRef.current) return;
      connectingRef.current = false;
      stopPolling();
      setBusyApp(null);
      loadStatuses();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadStatuses, stopPolling]);

  // Rueckfall-Trigger: Falls das Fokus-Ereignis in einer Konstellation ausbleibt,
  // fragt ein einfacher, zeitlich begrenzter Poll den Status beim Backend ab.
  const startFallbackPoll = useCallback((app: DatevApp) => {
    stopPolling();
    let attempts = 0;
    const maxAttempts = 40; // rund 100 Sekunden bei 2,5s Takt
    pollRef.current = setInterval(async () => {
      attempts += 1;
      if (attempts > maxAttempts) {
        stopPolling();
        return;
      }
      try {
        const status = await datevEmployeeService.getConnectionStatus(customerId, app);
        if (status.connected === true) {
          stopPolling();
          connectingRef.current = false;
          setStatusByApp(prev => ({ ...prev, [app]: status }));
          setBusyApp(null);
          onUpdate?.();
          loadStatuses();
        }
      } catch (err) {
        // transiente Fehler ignorieren
      }
    }, 2500);
  }, [customerId, loadStatuses, onUpdate, stopPolling]);

  const handleConnect = async (app: DatevApp) => {
    setError(null);
    if (!beraternummer || !mandantennummer) {
      setError('Bitte tragen Sie zuerst Beraternummer und Mandantennummer in den Stammdaten ein und speichern Sie.');
      return;
    }
    setBusyApp(app);
    try {
      const authUrl = await datevEmployeeService.getAuthorizationUrl(customerId, app, 'popup');
      const popup = window.open(
        authUrl,
        `datev_oauth_${app}`,
        'width=640,height=820,menubar=no,toolbar=no'
      );
      popupRef.current = popup;
      if (!popup) {
        setError('Das Popup wurde vom Browser blockiert. Bitte erlauben Sie Popups für diese Seite und versuchen Sie es erneut.');
        setBusyApp(null);
        return;
      }
      connectingRef.current = true;
      startFallbackPoll(app);
    } catch (err) {
      if (err instanceof DatevApiError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Fehler beim Verbindungsaufbau zu DATEV.');
      }
      setBusyApp(null);
    }
  };

  const handleDisconnect = async (app: DatevApp, label: string) => {
    if (!window.confirm(`DATEV-Verbindung „${label}" trennen? Die hinterlegten Zugangsdaten werden entfernt.`)) {
      return;
    }
    setError(null);
    setBusyApp(app);
    try {
      await datevEmployeeService.disconnect(customerId, app);
      await loadStatuses();
      onUpdate?.();
    } catch (err) {
      if (err instanceof DatevApiError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Fehler beim Trennen der DATEV-Verbindung.');
      }
    } finally {
      setBusyApp(null);
    }
  };

  const isConnected = (app: DatevApp): boolean => statusByApp[app]?.connected === true;

  return (
    <div>
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: '6px',
          marginBottom: '16px',
          color: '#991B1B',
          fontSize: '14px',
          lineHeight: '1.5'
        }}>
          {error}
        </div>
      )}

      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '8px'
      }}>
        <button
          onClick={() => loadStatuses()}
          disabled={loading}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            backgroundColor: 'transparent',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            color: '#374151',
            fontSize: '13px',
            fontWeight: '500',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          <RefreshCw size={14} />
          Aktualisieren
        </button>
      </div>

      <div style={{
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        {DATEV_APPS.map(({ app, label }, index) => {
          const connected = isConnected(app);
          const busy = busyApp === app;
          return (
            <div
              key={app}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                padding: '14px 16px',
                borderTop: index === 0 ? 'none' : '1px solid #F3F4F6'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                <span style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  flexShrink: 0,
                  backgroundColor: connected ? '#10B981' : '#D1D5DB'
                }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                    {label}
                  </div>
                  <div style={{ fontSize: '12px', color: connected ? '#059669' : '#6B7280' }}>
                    {loading ? 'Status wird geladen...' : busy ? 'Warte auf DATEV-Anmeldung...' : connected ? 'Verbunden' : 'Nicht verbunden'}
                  </div>
                </div>
              </div>
              <button
                onClick={() => connected ? handleDisconnect(app, label) : handleConnect(app)}
                disabled={busy || loading}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: connected ? '1px solid #D1D5DB' : 'none',
                  backgroundColor: busy || loading ? '#9CA3AF' : connected ? 'white' : '#3B82F6',
                  color: connected ? '#374151' : 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: busy || loading ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                {busy ? 'Bitte warten...' : connected ? 'Trennen' : 'Verbinden'}
              </button>
            </div>
          );
        })}
      </div>

      <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '12px', lineHeight: '1.5' }}>
        Der DATEV-Login öffnet sich in einem separaten Fenster. Nach erfolgreicher
        Anmeldung schließt es sich automatisch und der Status hier aktualisiert sich.
        Sollte er einmal nicht sofort umspringen, hilft „Aktualisieren".
      </p>
    </div>
  );
};
